/**
 * assetColumns.tsx
 * Sovereign-Emerald Antd v6 column definitions + AssetToggleStatusCell for the
 * Asset Catalog table. Mirrors features/user/components/userColumns.tsx.
 */

import { type FC, type ReactNode, useState } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'react-toastify';
import {
  Box,
  Edit,
  CheckCircle,
  XCircle as XCircleIcon,
  X,
  AlertTriangle,
  Calendar,
  User as UserIcon,
} from 'lucide-react';

import type { Asset, User as AssetUser } from '@/types/asset';
import { SOVEREIGN } from '@/features/epc/utils/epcProjectVisual';

const CATEGORY_LABEL: Record<string, string> = {
  generation: 'Generation',
  collection: 'Collection',
  conversion: 'Conversion',
  transmission: 'Transmission',
  connection: 'Connection',
  protection: 'Protection',
  structure: 'Structure',
  monitoring: 'Monitoring',
  safety: 'Safety',
  security: 'Security',
  other: 'Other',
};

function createdByName(createdBy: Asset['createdBy']): string {
  if (!createdBy) return '—';
  if (typeof createdBy === 'object' && 'name' in createdBy) return (createdBy as AssetUser).name;
  return '—';
}

export interface AssetColumnHandlers {
  onEdit: (a: Asset) => void;
  /** Render the status-toggle cell, typically wiring it to a mutation. */
  toggleCell: (a: Asset) => ReactNode;
}

export function buildAssetColumns(handlers: AssetColumnHandlers): ColumnsType<Asset> {
  const { onEdit, toggleCell } = handlers;

  return [
    // ── Code ───────────────────────────────────────────────────────────────
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      sorter: true,
      width: 120,
      render: (code: string) => (
        <span
          className="inline-block text-xs font-mono font-semibold px-2 py-0.5 rounded"
          style={{
            background: SOVEREIGN.inputBg,
            border: `1px solid ${SOVEREIGN.cardBorder}`,
            color: SOVEREIGN.ink,
            letterSpacing: '0.04em',
          }}
        >
          {code || 'Pending'}
        </span>
      ),
    },

    // ── Asset (icon + name) ──────────────────────────────────────────────────
    {
      title: 'Asset',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (_: unknown, asset: Asset) => {
        const isInactive = asset.status === 'inactive';
        const color = asset.architectureRules?.color;
        return (
          <div className="flex items-center gap-2.5 min-w-0">
            <span
              className="flex h-8 w-8 items-center justify-center rounded-lg shrink-0"
              aria-hidden
              style={{
                background: color ? `${color}1A` : 'rgba(31,90,71,0.08)',
                border: `1px solid ${color ? `${color}55` : SOVEREIGN.cardBorder}`,
                color: color || SOVEREIGN.bottle,
              }}
            >
              <Box size={15} aria-hidden />
            </span>
            <span
              className={`text-sm font-medium truncate ${isInactive ? 'line-through' : ''}`}
              style={{ color: isInactive ? SOVEREIGN.kpiCaption : SOVEREIGN.ink }}
            >
              {asset.name}
            </span>
          </div>
        );
      },
    },

    // ── Category ─────────────────────────────────────────────────────────────
    {
      title: 'Category',
      key: 'category',
      render: (_: unknown, asset: Asset) => {
        const cat = asset.architectureRules?.category;
        if (!cat) return <span className="text-sm" style={{ color: SOVEREIGN.kpiCaption }}>—</span>;
        return (
          <span
            className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded"
            style={{
              background: 'rgba(31,90,71,0.07)',
              border: '1px solid rgba(31,90,71,0.18)',
              color: SOVEREIGN.bottle,
            }}
          >
            {CATEGORY_LABEL[cat] ?? cat}
          </span>
        );
      },
    },

    // ── Created ──────────────────────────────────────────────────────────────
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      render: (createdAt: string | Date | undefined, asset: Asset) => (
        <div className="flex flex-col gap-0.5">
          <span className="flex items-center gap-1.5 text-sm" style={{ color: SOVEREIGN.kpiCaption }}>
            <Calendar size={13} aria-hidden style={{ flexShrink: 0 }} />
            {createdAt ? formatDistanceToNow(new Date(createdAt), { addSuffix: true }) : '—'}
          </span>
          <span className="flex items-center gap-1.5 text-xs" style={{ color: SOVEREIGN.kpiCaption }}>
            <UserIcon size={11} aria-hidden style={{ flexShrink: 0 }} />
            {createdByName(asset.createdBy)}
          </span>
        </div>
      ),
    },

    // ── Status ───────────────────────────────────────────────────────────────
    {
      title: 'Status',
      key: 'status',
      align: 'center',
      width: 110,
      render: (_: unknown, asset: Asset) => (
        <div className="flex items-center justify-center">{toggleCell(asset)}</div>
      ),
    },

    // ── Actions ──────────────────────────────────────────────────────────────
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 90,
      render: (_: unknown, asset: Asset) => (
        <div className="flex items-center justify-center">
          <button
            type="button"
            title="Edit asset"
            aria-label={`Edit ${asset.name}`}
            onClick={() => onEdit(asset)}
            className="inline-flex items-center justify-center w-7 h-7 rounded transition-colors duration-150"
            style={{ color: SOVEREIGN.kpiCaption, border: '1px solid transparent' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = SOVEREIGN.bottle;
              e.currentTarget.style.borderColor = SOVEREIGN.cardBorder;
              e.currentTarget.style.background = 'rgba(31,90,71,0.07)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = SOVEREIGN.kpiCaption;
              e.currentTarget.style.borderColor = 'transparent';
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <Edit size={15} aria-hidden />
          </button>
        </div>
      ),
    },
  ];
}

// ─── AssetToggleStatusCell ──────────────────────────────────────────────────

export interface AssetToggleStatusCellProps {
  asset: Asset;
  /** Page wires this to the mutation. The cell does NOT call the API itself. */
  onConfirm: () => Promise<void>;
}

export const AssetToggleStatusCell: FC<AssetToggleStatusCellProps> = ({ asset, onConfirm }) => {
  const [isOpen, setOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const isActive = asset.status === 'active';

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
      toast.success(`Asset ${isActive ? 'deactivated' : 'activated'} successfully`);
      setOpen(false);
    } catch {
      toast.error('Failed to update asset status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        title={`Click to ${isActive ? 'deactivate' : 'activate'}`}
        aria-label={`${isActive ? 'Deactivate' : 'Activate'} ${asset.name}`}
        onClick={() => setOpen(true)}
        className="relative inline-flex items-center w-10 h-5 rounded-full border select-none transition-all duration-300 focus:outline-none"
        style={
          isActive
            ? { background: 'rgba(31,90,71,0.10)', borderColor: 'rgba(31,90,71,0.30)' }
            : { background: 'rgba(124,138,130,0.10)', borderColor: 'rgba(124,138,130,0.25)' }
        }
      >
        <span
          aria-hidden
          className="w-3 h-3 rounded-full transform transition-all duration-300"
          style={
            isActive
              ? { transform: 'translateX(18px)', background: SOVEREIGN.bottle }
              : { transform: 'translateX(4px)', background: SOVEREIGN.kpiCaption }
          }
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center"
            style={{ background: 'rgba(42,31,23,0.18)' }}
            onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              className="relative w-full max-w-md mx-4 rounded-xl overflow-hidden"
              style={{
                background: SOVEREIGN.cardBg,
                border: `1px solid ${SOVEREIGN.cardBorder}`,
                boxShadow: SOVEREIGN.cardShadow,
              }}
              role="dialog"
              aria-modal
              aria-labelledby="asset-toggle-dialog-title"
            >
              {/* Header */}
              <div
                className="flex items-center justify-between px-5 py-4"
                style={{ borderBottom: `1px solid ${SOVEREIGN.innerRule}` }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                    aria-hidden
                    style={
                      isActive
                        ? { background: 'rgba(159,18,57,0.06)', border: '1px solid rgba(159,18,57,0.18)' }
                        : { background: 'rgba(31,90,71,0.07)', border: `1px solid ${SOVEREIGN.cardBorder}` }
                    }
                  >
                    {isActive ? (
                      <XCircleIcon size={18} style={{ color: '#9F1239' }} aria-hidden />
                    ) : (
                      <CheckCircle size={18} style={{ color: SOVEREIGN.bottle }} aria-hidden />
                    )}
                  </div>
                  <div>
                    <h2
                      id="asset-toggle-dialog-title"
                      className="text-base font-semibold"
                      style={{ color: SOVEREIGN.title }}
                    >
                      {isActive ? 'Deactivate Asset' : 'Activate Asset'}
                    </h2>
                    <p className="text-sm" style={{ color: SOVEREIGN.kpiCaption }}>
                      {asset.name}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  title="Close"
                  aria-label="Close dialog"
                  onClick={() => setOpen(false)}
                  disabled={isLoading}
                  className="inline-flex items-center justify-center w-7 h-7 rounded-lg transition-colors duration-150 disabled:opacity-50"
                  style={{
                    color: SOVEREIGN.kpiCaption,
                    border: `1px solid ${SOVEREIGN.cardBorder}`,
                    background: SOVEREIGN.inputBg,
                  }}
                >
                  <X size={14} aria-hidden />
                </button>
              </div>

              {/* Body */}
              <div className="px-5 py-4">
                <div className="flex items-start gap-3">
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                    aria-hidden
                    style={{ background: 'rgba(201,169,97,0.08)', border: '1px solid rgba(201,169,97,0.24)' }}
                  >
                    <AlertTriangle size={14} aria-hidden style={{ color: SOVEREIGN.champagne }} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm leading-relaxed mb-3" style={{ color: SOVEREIGN.ink }}>
                      Are you sure you want to{' '}
                      <span className="font-semibold">{isActive ? 'deactivate' : 'activate'}</span>{' '}
                      <span className="font-semibold" style={{ color: SOVEREIGN.title }}>{asset.name}</span>?
                    </p>
                    <div
                      className="px-3 py-2 rounded-lg text-xs leading-relaxed"
                      style={{
                        background: SOVEREIGN.inputBg,
                        border: `1px solid ${SOVEREIGN.innerRule}`,
                        color: SOVEREIGN.kpiCaption,
                      }}
                    >
                      {isActive
                        ? 'This asset will be hidden from selection in new project configurations.'
                        : 'This asset will become available for selection again.'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div
                className="flex items-center justify-end gap-2.5 px-5 py-4 rounded-b-xl"
                style={{ borderTop: `1px solid ${SOVEREIGN.innerRule}`, background: 'rgba(242,234,219,0.35)' }}
              >
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  disabled={isLoading}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-150 disabled:opacity-50"
                  style={{
                    color: SOVEREIGN.ink,
                    background: SOVEREIGN.cardBg,
                    border: `1px solid ${SOVEREIGN.cardBorder}`,
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={isLoading}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors duration-150 disabled:opacity-60"
                  style={
                    isActive
                      ? {
                          background: isLoading ? '#9F1239cc' : '#9F1239',
                          border: '1px solid rgba(159,18,57,0.45)',
                          boxShadow: '0 4px 10px -6px rgba(159,18,57,0.45)',
                        }
                      : {
                          background: isLoading ? `${SOVEREIGN.btn}cc` : SOVEREIGN.btn,
                          border: `1px solid ${SOVEREIGN.btnBorder}`,
                          boxShadow: SOVEREIGN.btnShadow,
                        }
                  }
                >
                  {isLoading ? (
                    <span
                      className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin"
                      aria-hidden
                    />
                  ) : null}
                  {isActive ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};
